<template>
  <section
    v-if="attractionTags?.length"
    ref="sightsBlock"
    class="sights-wrap"
  >
    <div class="wrap">
      <ToursSectionTitle
        :title="sightsTitle"
        :show-more="t('attraction_section.showAll')"
        :href="props.sightsUrl"
      />

      <div
        v-attractions-view-tracker="soguAdapteredData"
        class="sights slider-parent"
      >
        <UiSlider
          :slides="attractionTags"
          mousewheel
          arrows-always-visible
          :slides-per-view="slidesPerView"
          :space-between="16"
          :options="{
            slidesPerGroup: slidePerGroup,
          }"
        >
          <!-- Слот слайда использует индекс нового UiSlider для получения типизированного тега. -->
          <template #slide="{ index }">
            <ToursAttraction
              :attraction="attractionTags[index]"
              :attraction-url="buildAttractionUrl(attractionTags[index])"
              :default-sogu-fields="defaultSoguFields"
            />
          </template>
          <template #rightArrowIcon>
            <AttractionsArrowRightIcon size="24" />
          </template>
          <template #leftArrowIcon>
            <AttractionsArrowRightIcon
              size="24"
              class="mirror"
            />
          </template>
        </UiSlider>
      </div>
    </div>
    <div
      id="sights"
      class="fake-anchor"
    />
  </section>
</template>

<script lang="ts" setup>
  import type { CityInfo, CityTag, CountryInfo, RegionInfo, RegionTag } from '@/shared/api/experience';
  import { UiSlider } from '@experience/core-slider-ui';
  import { ToursAttraction } from '@/entities/attraction/ui/tour';
  import { useScreenSize, withoutDomain } from '@experience/shared';

  const { isMobile } = useScreenSize();

  import ru from '../../i18n/ru.json';
  import { useI18n } from '@experience/i18n';
  import { AttractionsArrowRightIcon } from '@/shared/ui/icons/attractionsArrowRightIcon';
  import { useSlidesPerView } from '@/shared/lib/use-slides-per-view';
  import { slidesPerViewConfig } from '@/widgets/attractionsToursBlock/lib/config';
  import { useRegionTag } from '@/entities/tag/api/useRegionTag';
  import { useCityTag } from '@/entities/tag/api/useCityTag';
  import type { ContextGeoSubject } from '@/shared/types/geography';
  import { computed, ref, watch } from 'vue';
  import { useViewTracker } from '@/entities/sogu';
  import { LocationType } from '@/entities/location';
  import { ToursSectionTitle } from '@/entities/sectionTitle/ui/tour';
  import { useSoguTags } from '@/entities/sogu/composables/useSoguTags.ts';
  import { getCityUrl } from '@/entities/rubrics';

  const { t } = useI18n({
    inheritLocale: true,
    messages: {
      ru,
    },
  });

  interface Props {
    slug: string;
    geoObject: CityInfo | CountryInfo | RegionInfo;
    sightsUrl: string;
    location: LocationType;
    defaultSoguFields: ContextGeoSubject;
  }

  type AttractionTag = CityTag | RegionTag;

  const props = defineProps<Props>();

  const sightsBlock = ref<HTMLElement | null>(null);

  const slidesPerView = ref(0);

  const slidePerGroup = computed(() => (isMobile.value ? 1 : slidesPerView.value));

  const { regionTags } = useRegionTag({
    slug: computed(() => props.slug),
    enabled: computed(() => props.location === LocationType.REGION),
  });

  const { cityTags } = useCityTag({
    slug: computed(() => props.slug),
    sorting: computed(() => '-tours_count'),
    enabled: computed(() => props.location === LocationType.CITY),
  });

  const tagsSource = computed<AttractionTag[] | undefined>(() => {
    if (props.location === LocationType.CITY) {
      return cityTags.value;
    }

    return regionTags.value;
  });

  const attractionTags = computed(() => {
    const tags = tagsSource.value;

    const arr = tags?.reduce<AttractionTag[]>((acc, tag) => {
      if (tag.flags?.is_attraction && !tag.flags?.is_seo_only && tag.images?.cover) {
        acc.push(tag);
      }

      return acc;
    }, []);

    const countKey = 'tours_count';

    return arr
      ?.filter((tag) => !!tag?.counters?.[countKey])
      ?.sort((a, b) => (b?.counters?.[countKey] || 0) - (a?.counters?.[countKey] || 0));
  });

  const { soguTags } = useSoguTags(
    computed(
      () =>
        attractionTags.value?.map(({ id, counters }) => {
          return { id, counter: counters?.tours_count };
        }) ?? [],
    ),
  );

  const slidesTotalCount = computed(() => attractionTags.value?.length || 0);

  const updateSlidesPerView = () => {
    slidesPerView.value = useSlidesPerView(slidesTotalCount.value, ...slidesPerViewConfig);
  };

  const sightsTitle = computed(() => t('attraction_section.title', { inObjPhrase: props.geoObject?.in_obj_phrase }));

  const buildAttractionUrl = (tag: AttractionTag): string => {
    if (props.location === LocationType.CITY) {
      return getCityUrl('tours', props.slug, tag.slug || 'all');
    }

    const domain = tag.urls?.tours;

    return domain ? withoutDomain(domain) : '';
  };

  watch(
    () => attractionTags.value,
    (newVal) => {
      if (newVal) {
        updateSlidesPerView();
      }
    },
    { immediate: true },
  );

  const soguAdapteredData = () => ({ ...props.defaultSoguFields.soguStamp, tag_ids: soguTags.value });

  const vAttractionsViewTracker = useViewTracker('listing:sights.view');
  onMounted(() => {
    updateSlidesPerView();
    window.addEventListener('resize', updateSlidesPerView);
  });

  onUnmounted(() => {
    window.removeEventListener('resize', updateSlidesPerView);
  });
</script>
<style scoped lang="scss">
  @use '@experience/design-tokens/typography' as typography;
  @use '@/shared/styles/components/slider' as slider;
  @use '@experience/shared/dist/assets/adaptivity' as adaptivity;

  .sights-wrap {
    position: relative;
    overflow: hidden;
    margin-top: 80px;
    padding: 40px 0;
    background: var(--alias-color-background-subtler);

    @include slider.mobile-slider;

    .feature-title {
      font-weight: 800;
    }

    .feature-title-link-more.sights-link-more {
      display: inline-block;
      color: var(--alias-color-link-default);
      text-decoration: none;

      @include typography.base-400-400-regular;

      @include adaptivity.tablet {
        display: none;
      }
    }

    .btn-wrap_more {
      display: none;
      margin-top: 24px;

      :deep(.button) {
        width: 100%;
      }

      @include adaptivity.mobile {
        margin-top: 16px;
      }

      @include adaptivity.tablet {
        display: block;
      }
    }

    @include adaptivity.tablet {
      margin-top: 56px;
    }

    @media only screen and (max-width: 680px) {
      padding: 28px 0;
    }
  }
</style>
